import type {
  QuestionnaireAnswerValue,
  QuestionnaireAnswersMap,
  QuestionnaireQuestion
} from "../../types";
import type { QuestionnaireHistoryItem } from "./questionnaire.types";

export const isAnswered = (question: QuestionnaireQuestion, value: QuestionnaireAnswerValue) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (question.type === "multi_select") {
    return Array.isArray(value) && value.length > 0;
  }
  return true;
};

export const toArray = (value: QuestionnaireAnswerValue) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

export const toNumberValue = (value: QuestionnaireAnswerValue) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const shouldShowQuestion = (
  question: QuestionnaireQuestion,
  answers: QuestionnaireAnswersMap
) => {
  if (!question.showWhen || question.showWhen.length === 0) return true;

  return question.showWhen.every((condition) => {
    const sourceAnswer = answers[condition.questionId];

    if (condition.operator === "equals") {
      if (Array.isArray(sourceAnswer)) {
        return sourceAnswer.includes(condition.value);
      }
      return sourceAnswer === condition.value;
    }

    if (condition.operator === "includes") {
      if (!Array.isArray(sourceAnswer)) return false;
      return sourceAnswer.includes(condition.value);
    }

    return true;
  });
};

export const buildAnswersFromSubmission = (submission?: QuestionnaireHistoryItem | null) => {
  const mapped: QuestionnaireAnswersMap = {};
  if (!submission) return mapped;
  submission.answers.forEach((answer) => {
    if (Array.isArray(answer.data) && answer.data.length > 0) {
      mapped[answer.questionId] = answer.data
        .map((item) => item.value)
        .filter(Boolean);
      return;
    }
    if (typeof answer.value === "string" && answer.value !== "") {
      mapped[answer.questionId] = answer.value;
    }
  });
  return mapped;
};

export const formatTimelineDateTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes >= 0 && diffMinutes < 1) {
    return "Now";
  }

  const timePart = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit"
  });

  const isSameDay = date.toDateString() === now.toDateString();
  if (isSameDay) {
    return `Today, ${timePart}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${timePart}`;
  }

  const datePart = date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
  return `${datePart}, ${timePart}`;
};
